import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { isStagingOrLocalhost } from '../utils/environment';
import { getChromeRuntime } from '../utils/extensionMessaging';
import { getExtensionId } from '../config/extension';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser } from '../utils/authSession';
import { MENU_ICON_PATHS } from '../components/menuIcons';
import FeedbackFab from '../components/FeedbackFab';

interface DomainGroupOption {
  id: string;
  name: string;
}

interface DomainUser {
  id: string;
  groupId?: string | null;
  role: string;
  rawRole?: string;
  isAdmin?: boolean;
}

const GROUP_ICON_PATHS = {
  rename: '/menu-icons/group-rename.png',
  delete: '/menu-icons/group-delete.png'
};

const AdminGroupsPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const usePersistentSidebar = isStagingOrLocalhost();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<DomainGroupOption[]>([]);
  const [users, setUsers] = useState<DomainUser[]>([]);
  const [saving, setSaving] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState('');
  const [editingGroupName, setEditingGroupName] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;

      const [groupsRes, usersRes] = await Promise.all([
        fetch(`${baseUrl}/admin/groups`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          cache: 'no-store'
        }),
        fetch(`${baseUrl}/admin/users`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          cache: 'no-store'
        })
      ]);

      const groupsData = await groupsRes.json().catch(() => ({}));
      const usersData = await usersRes.json().catch(() => ({}));

      if (!groupsRes.ok) throw new Error(groupsData.error || 'Failed to load groups');
      if (!usersRes.ok) throw new Error(usersData.error || 'Failed to load users');
      if (usersData.viewerIsSuperAdmin) {
        navigate('/admin/users');
        return;
      }

      setGroups(Array.isArray(groupsData.groups) ? groupsData.groups : []);
      setUsers(Array.isArray(usersData.users) ? usersData.users : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Groups - Traffic Lite Admin';
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }
    clearAllAuthStorage();
    const runtime = getChromeRuntime();
    if (runtime) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) runtime.sendMessage(extensionId, message, () => {});
        else runtime.sendMessage(message, () => {});
      } catch (sendError) {
        console.error('Sign-out storage clear failed:', sendError);
      }
    }
    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const sidebarItems: SideMenuItem[] = [
    { key: 'policy', label: 'Policy Details', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
    { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
    { key: 'groups', label: 'Groups', onClick: () => navigate('/admin/groups'), isActive: true, icon: MENU_ICON_PATHS.groups },
    { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard }
  ];

  const usersByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    users.forEach((user) => {
      const isTeacher = (user.rawRole || user.role) === 'teacher' && !user.isAdmin;
      if (!isTeacher || !user.groupId) return;
      counts[user.groupId] = (counts[user.groupId] || 0) + 1;
    });
    return counts;
  }, [users]);

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name || saving) return;
    setSaving(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) throw new Error('Not authenticated.');
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/groups`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to create group.');
      setNewGroupName('');
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create group.');
    } finally {
      setSaving(false);
    }
  };

  const handleRenameGroup = async (groupId: string) => {
    const name = editingGroupName.trim();
    if (!name || saving) return;
    setSaving(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) throw new Error('Not authenticated.');
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/groups/${groupId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to rename group.');
      setEditingGroupId('');
      setEditingGroupName('');
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to rename group.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    if (saving) return;
    const confirmDelete = window.confirm(`Delete "${groupName}"? Users will become "No Group".`);
    if (!confirmDelete) return;
    setSaving(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) throw new Error('Not authenticated.');
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to delete group.');
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete group.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className={usePersistentSidebar ? 'max-w-[1400px] mx-auto flex gap-6 items-start' : 'max-w-[1200px] mx-auto'}>
        {usePersistentSidebar && <SideMenuCard title="Admin Menu" items={sidebarItems} />}
        <div className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-lg shadow-md p-8`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <AdminHeader
            title="Traffic Lite Admin"
            subtitle="Groups"
            helperText="Create, rename, and delete groups, then jump to users or policy by group."
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />
          <ImpersonationBanner />

          {error && (
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#fee', border: '1px solid #fcc', color: '#c00' }}>
              {error}
            </div>
          )}

          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleCreateGroup();
                  }
                }}
                placeholder="New group name"
                style={{
                  minWidth: '260px',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={() => void handleCreateGroup()}
                disabled={saving || !newGroupName.trim()}
                style={{
                  padding: '8px 14px',
                  fontSize: '14px',
                  fontWeight: 700,
                  backgroundColor: saving || !newGroupName.trim() ? '#9e9e9e' : '#4285f4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving || !newGroupName.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                New Group
              </button>
            </div>
            <p className="text-gray-600">{groups.length} group{groups.length !== 1 ? 's' : ''}</p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading groups...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="portal-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#212529', borderBottom: '2px solid #212529' }}>
                    <th style={{ padding: '12px', color: 'white', fontWeight: 600, borderRight: '1px solid rgba(255,255,255,0.3)' }}>Group Name</th>
                    <th style={{ padding: '12px', color: 'white', fontWeight: 600, borderRight: '1px solid rgba(255,255,255,0.3)' }}>Users</th>
                    <th style={{ padding: '12px', color: 'white', fontWeight: 600, borderRight: '1px solid rgba(255,255,255,0.3)' }}>View Group Policy</th>
                    <th style={{ padding: '12px', color: 'white', fontWeight: 600, borderRight: '1px solid rgba(255,255,255,0.3)' }}>Edit Group</th>
                    <th style={{ padding: '12px', color: 'white', fontWeight: 600 }}>Delete Group</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group, index) => (
                    <tr
                      key={group.id}
                      style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                      }}
                    >
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                        {editingGroupId === group.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              value={editingGroupName}
                              onChange={(e) => setEditingGroupName(e.target.value)}
                              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                            <button
                              type="button"
                              onClick={() => void handleRenameGroup(group.id)}
                              disabled={saving || !editingGroupName.trim()}
                              style={{ border: 'none', background: 'transparent', color: '#0f766e', fontWeight: 700, cursor: 'pointer' }}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingGroupId('');
                                setEditingGroupName('');
                              }}
                              style={{ border: 'none', background: 'transparent', color: '#374151', fontWeight: 700, cursor: 'pointer' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span>{group.name}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const params = new URLSearchParams();
                            params.set('groupId', group.id);
                            if (group.name.trim()) {
                              params.set('q', group.name.trim());
                            }
                            navigate(`/admin/users?${params.toString()}`);
                          }}
                          style={{ border: 'none', background: 'transparent', color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {usersByGroup[group.id] || 0} users
                        </button>
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                        <button
                          type="button"
                          title="View Group Policy"
                          aria-label="View Group Policy"
                          className="icon-action-btn"
                          onClick={() => {
                            const url = `${window.location.origin}${window.location.pathname}#/admin/settings?groupId=${encodeURIComponent(group.id)}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <span
                            aria-hidden="true"
                            className="icon-action-glyph"
                            style={{
                              WebkitMaskImage: `url(${MENU_ICON_PATHS.policyDetails})`,
                              maskImage: `url(${MENU_ICON_PATHS.policyDetails})`
                            }}
                          />
                        </button>
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                        <button
                          type="button"
                          title="Rename Group"
                          aria-label="Rename Group"
                          className="icon-action-btn"
                          onClick={() => {
                            setEditingGroupId(group.id);
                            setEditingGroupName(group.name);
                          }}
                        >
                          <span
                            aria-hidden="true"
                            className="icon-action-glyph"
                            style={{
                              WebkitMaskImage: `url(${GROUP_ICON_PATHS.rename})`,
                              maskImage: `url(${GROUP_ICON_PATHS.rename})`
                            }}
                          />
                        </button>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          type="button"
                          title="Delete Group"
                          aria-label="Delete Group"
                          className="icon-action-btn"
                          onClick={() => void handleDeleteGroup(group.id, group.name)}
                        >
                          <span
                            aria-hidden="true"
                            className="icon-action-glyph"
                            style={{
                              WebkitMaskImage: `url(${GROUP_ICON_PATHS.delete})`,
                              maskImage: `url(${GROUP_ICON_PATHS.delete})`
                            }}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && groups.length === 0 && !error && (
            <div className="text-center py-12 text-gray-500">No groups yet. Create your first group above.</div>
          )}
          <FeedbackFab />
        </div>
      </div>
    </div>
  );
};

export default AdminGroupsPage;
